import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `stuck` becomes true once the sentinel has scrolled above `offset` px from the top of the viewport –
 * i.e. the sticky element that follows it is now pinned. Works for window scrolling and for a clipped
 * scroll container that starts at `offset` (IntersectionObserver honours ancestor clipping).
 *
 * Returns `[setRef, stuck, nodeRef]`: attach `setRef` as the sentinel's ref (callback ref – the sentinel may
 * mount after data has loaded), read `nodeRef.current` for imperative scrolling.
 */
export function useStuck(offset = 48) {
  const nodeRef = useRef(null);
  const [node, setNode] = useState(null);
  const [stuck, setStuck] = useState(false);
  const setRef = useCallback((el) => {
    nodeRef.current = el;
    setNode(el);
  }, []);
  useEffect(() => {
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([e]) => setStuck(!e.isIntersecting && e.boundingClientRect.top < offset + 1),
      { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [node, offset]);
  return [setRef, stuck, nodeRef];
}
